import React, { useEffect, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';

import { setSnackbar } from 'flow/actions/app-actions';
import { showCallDialog } from 'flow/actions/call-dialog-actions';
import {
    fetchAllMeetingNumbers,
    requestPin,
    rotateMeetingData,
} from 'flow/actions/meeting-actions';
import { getBrand } from 'flow/brands/brands';
import Button from 'flow/components/ui-components/Button';
import DropdownMenuComponent from 'flow/components/ui-components/DropdownMenuComponent';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import NumberWrapper from 'flow/components/ui-components/NumberWrapper';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import {
    selectAllMeetingNumbersSorted,
    selectViewerCanRegenerateLink,
} from 'flow/selectors/meeting-selectors';
import { getOwnCountryCodeOrNull, isRetailer } from 'flow/selectors/selectors';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { parseUrlParameter } from 'flow/utils/browser-utils';
import { IS_UAE_HOST } from 'flow/utils/browser-utils-TW';
import { ExtraHeaders, prettyFormatE164Number } from 'flow/utils/call-utils';
import {
    FREE_PRO_INTERNATIONAL_CONFERENCE_NUMBER,
    INTERNATIONAL_CONFERENCE_NUMBER,
} from 'flow/utils/meeting-constants';
import {
    formatPin,
    getConferenceLink,
    openMeetingInvitationMailWithPin,
} from 'flow/utils/meeting-utils';
import { Retailers } from 'flow/utils/retailer-utils';

interface Props {
    meetingCode: string;
    number: string;
    showDialButton: boolean;
    pinCode?: string;
    shouldShowVideo?: boolean;
    onCreateMeeting?: () => void;
    showQrCodes?: boolean;
    // The chat-service room this meetingCode is linked to, if any — needed so
    // the regenerate-link permission check is scoped to the meeting actually
    // being displayed here, not whatever meeting happens to be active.
    chatSessionKey?: ChatSessionKey;
}

interface RegenerateLinkButtonProps {
    onClick: () => void;
}

const RegenerateLinkButton = ({ onClick }: RegenerateLinkButtonProps) => {
    const { i18next } = useAppContext();

    return (
        <button className="text-link" onClick={onClick}>
            {i18next.t('videoconference.join_info.rotate_meeting_data.button')}
        </button>
    );
};

const MeetingJoinInfo = ({
    meetingCode,
    number,
    showDialButton,
    pinCode,
    shouldShowVideo,
    onCreateMeeting,
    showQrCodes = true,
    chatSessionKey,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { openModal } = useModal();

    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    const ownCountryCode = useAppSelector(getOwnCountryCodeOrNull);
    const chatSessionId = chatSessionKey?.replace('chatsession-', '');
    const canRegenerateLink = useAppSelector((state) =>
        selectViewerCanRegenerateLink(state, chatSessionId),
    );
    const meetingNumbers = useAppSelector(selectAllMeetingNumbersSorted);

    const isRetailerFreePro = useAppSelector((state) =>
        isRetailer(state, Retailers.FREEPRO),
    );
    const countryOptions = Array.from(meetingNumbers.values()).map(
        (meetingNumber) => ({
            key: meetingNumber.countryCode,
            label: meetingNumber.countryNameInEnglish,
        }),
    );

    const getInitialCountryCode = () => {
        if (IS_UAE_HOST) {
            return 'ae';
        }

        if (ownCountryCode) {
            return ownCountryCode.toLowerCase();
        }

        if (parseUrlParameter('countryCode')) {
            return parseUrlParameter('countryCode');
        }

        if (meetingNumbers) {
            return countryOptions[0].key;
        }

        return null;
    };

    const [countryCode, setCountryCode] = useState(getInitialCountryCode());
    const [countryNumber, setCountryNumber] = useState(
        number || INTERNATIONAL_CONFERENCE_NUMBER,
    );
    const [pin, setPin] = useState(pinCode?.replace(/ /g, '') || null);
    const [loadingPin, setLoadingPin] = useState(false);

    const [conferenceLink, setConferenceLink] = useState(null);

    useEffect(() => {
        if (shouldShowVideo) {
            setConferenceLink(getConferenceLink(meetingCode, countryCode));
        }
    }, [meetingCode, countryCode, shouldShowVideo]);

    useEffect(() => {
        dispatch(fetchAllMeetingNumbers());
    }, [dispatch]);

    useEffect(() => {
        setPin(pinCode?.replace(/ /g, ''));
    }, [pinCode]);

    useEffect(() => {
        if (countryCode === 'fr' && isRetailerFreePro) {
            setCountryNumber(FREE_PRO_INTERNATIONAL_CONFERENCE_NUMBER);
        } else {
            const country = meetingNumbers?.get(countryCode);
            if (country) {
                const prettyNumber = prettyFormatE164Number(
                    country.conferenceNumber,
                    'wantInternational',
                );
                setCountryNumber(prettyNumber);
            }
        }
    }, [meetingNumbers, countryCode, isRetailerFreePro]);

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            dispatch(
                setSnackbar({
                    message: i18next.t('snackbar.copied_to_clipboard'),
                }),
            );
        });
    };

    const requestConferencePin = async () => {
        setLoadingPin(true);
        const resultPin = await dispatch(requestPin(meetingCode));
        setLoadingPin(false);
        setPin(resultPin?.replace(/ /g, ''));
        return resultPin;
    };

    const callMeetingWithPin = async () => {
        const prettyPin = pin
            ? pin.replace(/ /g, '')
            : await requestConferencePin();
        dispatch(
            showCallDialog(number, undefined, {
                extraHeaders: [
                    `${ExtraHeaders.CONFERENCE_PIN.value}: ${prettyPin}`,
                ],
            }),
        );
    };

    const moreNumbersString = getBrand().brandConfNumberUrl
        ? `${i18next.t('videoconference.email.body.more_numbers')} ${getBrand().brandConfNumberUrl}`
        : '';

    const body = [
        i18next.t('videoconference.email.body.invitation'),
        shouldShowVideo &&
            `${i18next.t('videoconference.email.body.join_video')} ${conferenceLink}`,
        `${i18next.t('videoconference.email.body.join_phone')} ${number}, ${pin}`,
        moreNumbersString,
    ]
        .filter(Boolean)
        .join('\n\n');

    return (
        <div className="box-border flex size-full flex-col gap-6 rounded-sm border">
            <div className="flex flex-col gap-4 px-6 pt-6">
                <div className="flex flex-col gap-1">
                    <DropdownMenuComponent
                        options={countryOptions}
                        title={
                            countryOptions.find(
                                (option) => option.key === countryCode,
                            )?.label
                        }
                        onItemSelected={(countryCode) => {
                            setCountryCode(countryCode);
                        }}
                        disabled={IS_UAE_HOST}
                    />
                </div>

                <div className="flex w-full flex-col gap-3">
                    <div className="flex w-full items-center gap-2 font-bold text-gray-800">
                        <span>
                            {i18next.t('videoconference.join_info.number')}
                        </span>

                        <span className="font-normal">
                            <NumberWrapper>{countryNumber}</NumberWrapper>
                        </span>

                        {showDialButton ? (
                            <IconButton
                                aria-label={i18next.t('dialpad.call')}
                                onClick={callMeetingWithPin}
                                className="bg-green ms-auto flex size-8 items-end"
                            >
                                <Icon iconKey="phone" className="text-white" />
                            </IconButton>
                        ) : null}
                    </div>

                    <div className="flex w-full items-center gap-2 font-bold text-gray-800">
                        <span>
                            {i18next.t('videoconference.join_info.pin')}
                        </span>

                        {pin ? (
                            <>
                                <span className="font-normal">
                                    <NumberWrapper>
                                        {formatPin(pin)}
                                    </NumberWrapper>
                                </span>
                                <IconButton
                                    aria-label={i18next.t('Tooltip.Title.Copy')}
                                    onClick={() => copyToClipboard(pin)}
                                    className="ms-auto size-8 items-end"
                                >
                                    <Icon
                                        iconKey="copy"
                                        className="text-gray-600"
                                    />
                                </IconButton>
                            </>
                        ) : (
                            <div className="ms-auto">
                                <Button
                                    color="ghost"
                                    onClick={requestConferencePin}
                                    className="flex gap-2 leading-4 ring-gray-300"
                                    disabled={loadingPin}
                                >
                                    <Icon iconKey="visibility" size="1rem" />
                                    {i18next.t(
                                        'videoconference.join_info.show_pin',
                                    )}
                                </Button>
                            </div>
                        )}
                    </div>

                    {shouldShowVideo && (
                        <div className="flex w-full items-center gap-2 font-bold text-gray-800">
                            <span>
                                {i18next.t('videoconference.join_info.link')}
                                {':'}
                            </span>

                            <span className="truncate font-normal">
                                {conferenceLink}
                            </span>

                            <IconButton
                                aria-label={i18next.t('Tooltip.Title.Copy')}
                                onClick={() => copyToClipboard(conferenceLink)}
                                className="ms-auto size-8 items-end"
                            >
                                <Icon
                                    iconKey="copy"
                                    className="text-gray-600"
                                />
                            </IconButton>
                        </div>
                    )}

                    {showQrCodes && (pin || shouldShowVideo) && (
                        <div className="font-bold">
                            {i18next.t('videoconference.join_info.qr_codes')}
                        </div>
                    )}
                </div>
            </div>

            {showQrCodes && (
                <div className="flex gap-6 px-6">
                    {!!pin && (
                        <div className="box-border flex grow flex-col justify-items-center rounded-sm border">
                            {/* Do not add space between number and pin, it will prevent auto DTMF */}
                            <div className="w-full border-b py-2 text-center font-bold">
                                {i18next.t('videoconference.join_info_call_in')}
                            </div>
                            <div className="flex w-full justify-center p-3">
                                <QRCodeCanvas
                                    value={`tel:${countryNumber},${pin}`}
                                    size={150}
                                />
                            </div>
                        </div>
                    )}

                    {shouldShowVideo && (
                        <div className="box-border flex grow flex-col justify-items-center rounded-sm border">
                            <div className="w-full border-b py-2 text-center font-bold">
                                {i18next.t(
                                    'videoconference.join_info_join_with_video',
                                )}
                            </div>
                            <div className="flex w-full justify-center p-3">
                                {conferenceLink && (
                                    <QRCodeCanvas
                                        value={conferenceLink}
                                        size={150}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
            {ownExtensionKey && shouldShowVideo && canRegenerateLink ? (
                <div className="px-6">
                    <RegenerateLinkButton
                        onClick={() =>
                            openModal('confirmationDialog', {
                                title: i18next.t(
                                    'videoconference.join_info.rotate_meeting_data.header',
                                ),
                                description: (
                                    <>
                                        {i18next.t(
                                            'videoconference.join_info.rotate_meeting_data.description',
                                        )}
                                        <br />
                                        <span className="font-bold">
                                            {i18next.t(
                                                'videoconference.join_info.rotate_meeting_data.warning',
                                            )}
                                        </span>
                                    </>
                                ),
                                confirmButtonText: i18next.t(
                                    'videoconference.join_info.rotate_meeting_data.confirm',
                                ),
                                confirmButtonColor: 'danger',
                                handleAccept: () =>
                                    dispatch(rotateMeetingData(meetingCode)),
                            })
                        }
                    />
                </div>
            ) : null}

            <div className="flex flex-wrap gap-3 border-t p-6">
                {onCreateMeeting && (
                    <Button
                        type="button"
                        color="ghost"
                        onClick={onCreateMeeting}
                        className="flex gap-2 leading-4 ring-gray-300"
                    >
                        <Icon iconKey="calendar_today" size="1rem" />
                        {i18next.t('meetings.instant_meeting.create_meeting')}
                    </Button>
                )}
                <Button
                    color="ghost"
                    onClick={async () => {
                        await openMeetingInvitationMailWithPin(
                            meetingCode,
                            pin,
                            countryNumber,
                            i18next,
                            shouldShowVideo,
                            countryCode,
                        );
                    }}
                    className="flex gap-2 leading-4 ring-gray-300"
                >
                    <Icon iconKey="email" size="1rem" />
                    {i18next.t('videoconference.join_info.send_email')}
                </Button>

                {shouldShowVideo && (
                    <Button
                        color="ghost"
                        onClick={() => copyToClipboard(body)}
                        className="flex gap-2 leading-4 ring-gray-300"
                    >
                        <Icon iconKey="copy" size="1rem" />
                        {i18next.t(
                            'videoconference.join_info.copy_joining_info',
                        )}
                    </Button>
                )}
            </div>
        </div>
    );
};

export default MeetingJoinInfo;
